import { formatDate } from '@frontend/mwm-ui'
import { yupResolver } from '@hookform/resolvers/yup'
import { captureException } from '@sentry/react'
import { useQueryClient } from '@tanstack/react-query'
import classNames from 'classnames'
import { QRCodeSVG } from 'qrcode.react'
import { useEffect, useRef, useState } from 'react'
import { useForm } from 'react-hook-form'
import { MdAdd, MdChatBubbleOutline, MdContentCopy, MdOutlineStar } from 'react-icons/md'
import { useNavigate } from 'react-router-dom'
import ReactSelectCreatable from 'react-select/creatable'
import * as Yup from 'yup'

import type { ReactElement } from 'react'
import type { DropzoneRef } from 'react-dropzone'
import type { MobileDeviceType } from '~/types/device-type'
import type { IFileUploaded } from '~/types/file'

import ClusterAppExternalLink from './ClusterAppExternalLink'
import InfosForm from './InfosForm'

import CompanyContactModal from '../modals/CompanyContactModal'

import AppLogo from '~/components/AppLogo'
import Button from '~/components/Buttons/Button'
import LinkButton from '~/components/Buttons/LinkButton'
import UploadFileInput from '~/components/Inputs/UploadFileInput'
import MwMLoader from '~/components/MwMLoader'
import Section from '~/components/Section'
import VideoPreview from '~/components/VideoPreview'
import { FORM_ERRORS } from '~/const'
import { useRequiredParams } from '~/hooks/useRequiredParams'
import { useSearchParamsContext } from '~/providers/SearchParams'
import {
  useGetAppFromClusterQuery,
  useSetTagsMutation,
  useTagsQuery,
  keys as clustersKeys,
  useSetAppVideosMutation,
} from '~/queries/clusters'
import { useFilesDetailsQuery } from '~/queries/files'
import { forgeDataAiUrl } from '~/utils/clusters'
import { copyText } from '~/utils/navigator'
import { formatDecimalNumber } from '~/utils/number'
import { displayToast } from '~/utils/toast'

import './styles.scss'

interface TagsFormValues {
  tags: string[]
}

interface VideosFormValues {
  videos: IFileUploaded[]
}

const validationSchema = Yup.object().shape({
  tags: Yup.array().of(Yup.string().required(FORM_ERRORS.REQUIRED)).required(),
})

const ClusterAppInfos = (): ReactElement => {
  /** Local state */

  const navigate = useNavigate()

  const { dispatchToSearchParams } = useSearchParamsContext()

  const queryClient = useQueryClient()

  const { clusterId, deviceType, storeId } = useRequiredParams<{
    clusterId: string
    deviceType: MobileDeviceType
    storeId: string
  }>()

  const dropzoneRef = useRef<DropzoneRef>(null)

  const [isUploading, setIsUploading] = useState(false)

  const [isContactModalOpen, setIsContactModalOpen] = useState(false)

  /** Queries & Mutations */

  const { data: appData, isLoading } = useGetAppFromClusterQuery(clusterId, deviceType, storeId)

  const { data: tagsData } = useTagsQuery(clusterId)

  const { data: videoDetails, isLoading: isVideoDetailsLoading } = useFilesDetailsQuery(appData?.video_file_ids || [])

  const { mutateAsync: setTags } = useSetTagsMutation()

  const { mutateAsync: setVideos } = useSetAppVideosMutation()

  /** Query dependent state */

  const tagsDefaultValues: TagsFormValues = { tags: appData?.tags || [] }

  const {
    formState: { isDirty, isSubmitting },
    handleSubmit,
    setValue,
  } = useForm<TagsFormValues>({
    defaultValues: tagsDefaultValues,
    resolver: yupResolver(validationSchema),
  })

  const tagsOptions = tagsData
    ? tagsData.sort((a, b) => a.toLowerCase().localeCompare(b.toLowerCase())).map((el) => ({ label: el, value: el }))
    : []

  const videosDefaultValues = { videos: videoDetails }

  const {
    formState: { isDirty: isVideosDirty, isSubmitting: isVideosSubmitting },
    handleSubmit: handleVideosSubmit,
    reset: resetVideos,
    setValue: setVideosValue,
    watch: videosWatch,
  } = useForm<VideosFormValues>({
    defaultValues: videosDefaultValues,
  })

  const videosFormValues = videosWatch('videos')

  /** Handlers */

  const handleContactButtonClick = () => setIsContactModalOpen(true)

  const handleContactModalState = (isOpen: boolean) => () => setIsContactModalOpen(isOpen)

  const handleCopyDataAIClick = (value: number) => () => copyText(`${value}`)

  const handleLoadingChange = (isLoading: boolean) => setIsUploading(isLoading)

  /// TAGS
  const handleTagsSelectChange = (value: unknown) => {
    setValue(
      'tags',
      (value as IOption[]).map(({ value }) => value),
      {
        shouldDirty: true,
        shouldTouch: true,
      }
    )
  }

  const handleTagsFormSubmit = async ({ tags }: TagsFormValues) => {
    if (clusterId && deviceType && storeId) {
      try {
        await setTags({ clusterId, deviceType, storeId, tags })
        await queryClient.invalidateQueries({
          queryKey: clustersKeys.clusterAppDetails(clusterId, deviceType, storeId),
        })
        await queryClient.invalidateQueries({ queryKey: clustersKeys.tags(clusterId) })
        displayToast('Tags successfully edited', 'success')
      } catch (e) {
        captureException(e)
        displayToast('Tags could not be edited', 'error')
      }
    }
  }

  /// VIDEOS
  const handleVideoDrop = (files: IFileUploaded[]) =>
    setVideosValue('videos', videosFormValues ? [...videosFormValues, ...files] : files, {
      shouldDirty: true,
      shouldTouch: true,
    })

  const handleAddVideosClick = () => dropzoneRef.current && dropzoneRef.current.open()

  const handleVideoDeleteClick = (videoId: string) => () => {
    const filteredVideos = videosFormValues.filter(({ id }) => id !== videoId)

    filteredVideos.length === 0
      ? resetVideos({ videos: filteredVideos })
      : setVideosValue('videos', filteredVideos, {
          shouldDirty: true,
          shouldTouch: true,
        })
  }

  const handleVideosFormSubmit = async (values: VideosFormValues) => {
    if (appData) {
      try {
        const uploadedVideosIds: string[] = values.videos.map(({ id }) => id)

        await setVideos({ deviceType: appData.device_type, storeId, videoIds: uploadedVideosIds })
        await queryClient.invalidateQueries({
          queryKey: clustersKeys.clusterAppDetails(clusterId, deviceType, storeId),
        })
        displayToast('Video(s) successfully updated', 'success')
      } catch (e) {
        captureException(e)
        displayToast('Video(s) could not be updated', 'error')
      }
    }
  }

  const handleVersionHistoryClick = (externalId: string) => () => {
    dispatchToSearchParams({ type: 'update', value: { externalId } })
    navigate('/versions')
  }

  /** Effects */

  useEffect(() => {
    if (videoDetails) {
      resetVideos({ videos: videoDetails })
    }
  }, [resetVideos, videoDetails])

  /** Render */

  return (
    <div className="cluster-app-infos-view">
      <Section>
        {isLoading ? (
          <MwMLoader category="general" />
        ) : appData ? (
          <>
            <div className="cluster-app-infos-view__header">
              <div className="cluster-app-infos-view__header__left-section">
                <div className="cluster-app-infos-view__header__left-section__app-id-card">
                  <AppLogo
                    appLogoUrl={appData.icon_url || undefined}
                    appPlatform={appData.device_type || undefined}
                    className="app-icon"
                    size="medium"
                  />
                  <div className="cluster-app-infos-view__header__left-section__app-id-card__name-container">
                    <div className="cluster-app-infos-view__header__left-section__app-id-card__name-container__name">
                      {appData.name}
                    </div>
                    <div className="cluster-app-infos-view__header__left-section__app-id-card__name-container__category">
                      {appData.category}
                    </div>
                    <div className="cluster-app-infos-view__header__left-section__app-id-card__name-container__badges-container">
                      {appData.avg_ratings ? (
                        <div className="cluster-app-infos-view__header__left-section__app-id-card__name-container__badges-container__badge">
                          <MdOutlineStar color="#ffaa00" size={12} />
                          <span>{formatDecimalNumber(appData.avg_ratings)}</span>
                        </div>
                      ) : null}
                      {appData.product_id_data_ai ? (
                        <div
                          className="cluster-app-infos-view__header__left-section__app-id-card__name-container__badges-container__badge pointer-cursor"
                          onClick={handleCopyDataAIClick(appData.product_id_data_ai)}
                        >
                          <span>{appData.product_id_data_ai}</span>
                          <MdContentCopy className="company-data-ai-product-id__copy-icon" size={11} />
                        </div>
                      ) : null}
                      {appData.publisher_name ? (
                        <div className="cluster-app-infos-view__header__left-section__app-id-card__name-container__badges-container__badge">
                          <span>{appData.publisher_name}</span>
                        </div>
                      ) : null}
                      {appData.last_version_date ? (
                        <div className="cluster-app-infos-view__header__left-section__app-id-card__name-container__badges-container__badge">
                          <span>Update {formatDate(appData.last_version_date)}</span>
                        </div>
                      ) : null}
                    </div>
                  </div>
                </div>
              </div>
              <div className="cluster-app-infos-view__header__right-section">
                <div className="cluster-app-infos-view__header__right-section__links-container">
                  {appData.company_email || appData.company_phone_number ? (
                    <div className="cluster-app-external-link" onClick={handleContactButtonClick}>
                      <label className="cluster-app-external-link__label">Contact</label>
                      <MdChatBubbleOutline size={12} />
                    </div>
                  ) : null}
                  {appData.store_url ? <ClusterAppExternalLink href={appData.store_url} label="App Store" /> : null}
                  <ClusterAppExternalLink href={forgeDataAiUrl(deviceType, storeId)} label="Data.ai" />
                </div>
                <LinkButton
                  backgroundColor="light"
                  label="Show versions history"
                  onClick={handleVersionHistoryClick(appData.store_id)}
                  size="small"
                />
                {appData.store_url ? (
                  <div className="cluster-app-infos-view__header__right-section__qr-code-wrapper">
                    <QRCodeSVG level="Q" size={70} value={appData.store_url} />
                  </div>
                ) : null}
              </div>
            </div>
            <div className="cluster-app-infos-view__actions-container">
              <div className="cluster-app-infos-view__actions-container__action">
                <div className="cluster-app-infos-view__actions-container__action__title">Videos</div>
                {isVideoDetailsLoading ? (
                  <MwMLoader category="item" />
                ) : (
                  <form
                    className="cluster-app-infos-view__actions-container__action__wrapper"
                    onSubmit={handleVideosSubmit(handleVideosFormSubmit)}
                  >
                    <div
                      className={classNames('videos-container', {
                        'no-files': !videosFormValues || videosFormValues.length === 0,
                      })}
                    >
                      {videosFormValues?.map(({ download_url, id, name }) => (
                        <div className="video-and-delete-wrapper" key={id}>
                          <button className="delete-video-button" onClick={handleVideoDeleteClick(id)} type="button">
                            X
                          </button>
                          <VideoPreview filename={name} url={download_url} />
                        </div>
                      ))}
                    </div>
                    <UploadFileInput
                      accept={{ 'video/*': [] }}
                      className={classNames('app-infos-videos-dropzone', {
                        'has-files': videosFormValues && videosFormValues.length > 0,
                      })}
                      dropzoneRef={dropzoneRef}
                      onDrop={handleVideoDrop}
                      onLoadingChange={handleLoadingChange}
                    />
                    <div className="cluster-app-infos-view__actions-container__action__wrapper__update-button-wrapper">
                      {videosFormValues && videosFormValues.length > 0 ? (
                        <Button
                          className="add-more-videos-button"
                          disabled={isUploading || isVideosSubmitting}
                          onClick={handleAddVideosClick}
                          type="button"
                        >
                          {isUploading ? <div className="lds-dual-ring" /> : <MdAdd color="#fff" size={20} />}
                        </Button>
                      ) : null}
                      <Button disabled={!isVideosDirty || isUploading || isVideosSubmitting} type="submit">
                        Update
                      </Button>
                    </div>
                  </form>
                )}
              </div>
              <div className="cluster-app-infos-view__actions-container__action">
                <div className="cluster-app-infos-view__actions-container__action__title">Tags</div>
                <form
                  className="cluster-app-infos-view__actions-container__action__wrapper"
                  onSubmit={handleSubmit(handleTagsFormSubmit)}
                >
                  <ReactSelectCreatable
                    className="cluster-app-infos-view__actions-container__action__wrapper__tags-input"
                    classNamePrefix="react-select-creatable"
                    defaultValue={appData.tags?.map((el) => ({ label: el, value: el }))}
                    isMulti
                    name="tags"
                    onChange={(value) => handleTagsSelectChange(value as IOption[])}
                    openMenuOnClick={(tagsData && tagsData.length > 0) || false}
                    options={tagsOptions}
                    placeholder='Type a tag and press "Enter"'
                  />
                  <div className="cluster-app-infos-view__actions-container__action__wrapper__update-button-wrapper">
                    <Button disabled={!isDirty || isSubmitting}>Update</Button>
                  </div>
                </form>
              </div>
              <div className="cluster-app-infos-view__actions-container__screenshots-wrapper">
                {appData.screenshot_urls
                  ?.slice(0, 5)
                  .map((url, index) => (
                    <img
                      alt={`preview ${index + 1}`}
                      className="cluster-app-infos-view__actions-container__screenshots-wrapper__screenshot"
                      key={index}
                      src={url}
                    />
                  ))}
              </div>
            </div>
            <div className="cluster-app-infos-view__form-and-tabs-container">
              <InfosForm mutationParams={{ clusterId, deviceType, storeId }} />
            </div>
          </>
        ) : (
          <div>No data available for this application</div>
        )}
      </Section>
      {appData?.company_email || appData?.company_phone_number ? (
        <CompanyContactModal
          email={appData.company_email || ''}
          isOpen={isContactModalOpen}
          onClose={handleContactModalState(false)}
          phone={appData.company_phone_number || ''}
        />
      ) : null}
    </div>
  )
}

export default ClusterAppInfos
